import { useMemo, type ReactNode } from 'react';
import { Building2, CalendarClock, CreditCard, FileText, Send, Users } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { LocationBadge } from '@/components/shared/location-badge';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/use-permissions';
import { useLocationStore } from '@/stores/location-store';
import { useModules } from '@/hooks/use-modules';
import { useSPDashboard, useSPPayees } from '@/hooks/use-send-payments';
import { useRecurringPayments, RECURRING_PAYMENT_FREQUENCY_LABELS } from '@/hooks/use-recurring-payments';
import { useVendorInvoiceBills } from '@/hooks/use-vendor-invoices';
import { formatCurrency, formatNumber, formatShortDate } from '@/lib/utils';
import { ActivityList, BarList, CardSkeleton, KpiCard, Panel, PaymentSplit } from './home-cards';

const SP = '#/send-payments';
const LIST_SHOWN = 5;

function payeeLabel(p?: { name: string | null; company: string | null } | null): string {
  return p?.company || p?.name || 'Payee';
}

/**
 * The Send Payments home: what has gone out, what's pending or failed, who
 * gets paid most, and what's coming up. Numbers come from the same sendpayment
 * endpoints the Send Payments page uses.
 *
 * The sendpayment dashboard endpoint aggregates across the whole business, so
 * the headline figures are tenant-wide (same as the Send Payments page's
 * stat cards); the upcoming/bills lists are scoped to the selected location.
 */
export function SendPaymentsHome({ switcher }: { switcher?: ReactNode }) {
  const { user } = useAuthStore();
  const { selectedLocationId } = useLocationStore();
  const locationId = selectedLocationId || undefined;
  const firstName = user?.name?.split(' ')[0] || 'there';

  const dashboard = useSPDashboard({ locationId });
  const dash = dashboard.data?.data;

  // The lists need their screen's View; without it the card is left out.
  const permissions = usePermissions();
  const canSeeRecurring = permissions.has('sp.recurring.view');
  const canSeeBills = permissions.has('sp.bills.view');
  const recurring = useRecurringPayments({ locationId, status: 'active', limit: 100 }, { enabled: canSeeRecurring });
  // Payee names label the recurring list.
  const payees = useSPPayees(undefined, { enabled: canSeeRecurring && permissions.has('sp.payees.view') });

  // Bills only exist where the invoice processor is switched on for the
  // location; the endpoint errors otherwise, so don't ask.
  const { data: modulesData } = useModules(locationId);
  const invoiceEnabled =
    modulesData?.data?.find((m) => m.moduleName === 'invoice_processor')?.superAdminEnabled === true;
  const bills = useVendorInvoiceBills({ locationId }, { enabled: invoiceEnabled && canSeeBills });

  const split = (rows: { label: string; amount: string }[]) => {
    const total = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    return {
      total,
      rows: rows
        .map((r) => ({
          method: r.label,
          total: Number(r.amount || 0),
          percent: total ? (Number(r.amount || 0) / total) * 100 : 0,
        }))
        .filter((r) => r.total > 0)
        .sort((a, b) => b.total - a.total),
    };
  };
  const statusRows = useMemo(
    () => split((dash?.payments.byStatus ?? []).map((r) => ({ label: r.status, amount: r.amount }))),
    [dash],
  );
  const methodRows = useMemo(
    () => split((dash?.payments.byMethod ?? []).map((r) => ({ label: r.method.toUpperCase(), amount: r.amount }))),
    [dash],
  );

  const pendingAmount = (dash?.payments.byStatus ?? [])
    .filter((r) => ['pending', 'processing', 'submitted'].includes(r.status.toLowerCase()))
    .reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const failedAmount = (dash?.payments.byStatus ?? [])
    .filter((r) => ['failed', 'returned'].includes(r.status.toLowerCase()))
    .reduce((sum, r) => sum + Number(r.amount || 0), 0);

  const payeeNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of payees.data?.data ?? []) map.set(p.id, p.company || p.name);
    return map;
  }, [payees.data]);

  const upcoming = useMemo(() => {
    const rows = recurring.data?.data ?? [];
    return [...rows]
      .sort((a, b) => Date.parse(a.nextRunAt) - Date.parse(b.nextRunAt))
      .slice(0, LIST_SHOWN)
      .map((r) => ({
        id: r.id,
        primary: payeeNames.get(r.payeeId) ?? 'Payee',
        secondary: RECURRING_PAYMENT_FREQUENCY_LABELS[r.frequency] ?? r.frequency,
        value: formatCurrency(r.amount),
        meta: `Next ${formatShortDate(r.nextRunAt)}`,
      }));
  }, [recurring.data, payeeNames]);

  const billsDue = useMemo(() => {
    if (!invoiceEnabled) return [];
    const today = new Date().toISOString().slice(0, 10);
    return (bills.data?.data ?? [])
      .filter((b) => b.status === 'approved')
      .sort((a, b) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'))
      .slice(0, LIST_SHOWN)
      .map((b) => {
        const past = !!b.dueDate && b.dueDate.slice(0, 10) < today;
        return {
          id: b.id,
          primary: b.payee?.company || b.payee?.name || b.vendorNameRaw || 'Vendor',
          secondary: b.invoiceNumber ? `Invoice ${b.invoiceNumber}` : (b.billNumber ?? undefined),
          value: formatCurrency(b.totalAmount),
          meta: b.dueDate ? `${past ? 'Overdue' : 'Due'} ${formatShortDate(b.dueDate)}` : 'No due date',
          alert: past,
        };
      });
  }, [bills.data, invoiceEnabled]);

  return (
    <div className="space-y-6">
      <PageHeader title={`Welcome back, ${firstName}`} description="How your vendor payments are doing right now">
        <LocationBadge />
        {switcher}
      </PageHeader>

      {/* ── headline figures ─────────────────────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {dashboard.isLoading || !dash ? (
          <>
            <CardSkeleton height="h-36" />
            <CardSkeleton height="h-36" />
            <CardSkeleton height="h-36" />
            <CardSkeleton height="h-36" />
          </>
        ) : (
          <>
            <KpiCard
              label="Total Paid"
              value={formatCurrency(dash.payments.completedAmount)}
              footnote={`${formatNumber(dash.payments.completed)} completed payments`}
              series={[]}
              dataKey="value"
            />
            <KpiCard
              label="Pending"
              value={formatCurrency(pendingAmount)}
              footnote={`${formatNumber(dash.payments.pending)} payments in progress`}
              series={[]}
              dataKey="value"
            />
            <KpiCard
              label="Failed / Returned"
              value={formatCurrency(failedAmount)}
              footnote={`${formatNumber(dash.payments.failed)} payments failed`}
              series={[]}
              dataKey="value"
              invert
            />
            <KpiCard
              label="Active Payees"
              value={formatNumber(dash.payees.active)}
              footnote={`${formatNumber(dash.payees.total)} payees in total`}
              series={[]}
              dataKey="value"
            />
          </>
        )}
      </div>

      {/* ── mix ───────────────────────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel
          title="Payments by Status"
          href={`${SP}?tab=payments`}
          requires="sp.transactions.view"
          hint="Across all locations."
        >
          {dashboard.isLoading ? (
            <CardSkeleton height="h-40" />
          ) : (
            <PaymentSplit
              rows={statusRows.rows}
              total={statusRows.total}
              empty={{
                icon: Send,
                title: 'No payments yet',
                description: 'Payment amounts split by status appear here.',
              }}
            />
          )}
        </Panel>
        <Panel
          title="Payments by Method"
          href={`${SP}?tab=payments`}
          requires="sp.transactions.view"
          hint="Across all locations."
        >
          {dashboard.isLoading ? (
            <CardSkeleton height="h-40" />
          ) : (
            <PaymentSplit
              rows={methodRows.rows}
              total={methodRows.total}
              empty={{
                icon: CreditCard,
                title: 'No payments yet',
                description: 'ACH and wire totals appear here as a split.',
              }}
            />
          )}
        </Panel>
      </div>

      {/* ── who and what ──────────────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Top Vendors" href={`${SP}?tab=payees`} requires="sp.payees.view">
          {dashboard.isLoading ? (
            <CardSkeleton height="h-40" />
          ) : (
            <BarList
              rows={(dash?.topVendors ?? []).map((v) => ({
                id: v.payeeId,
                name: payeeLabel(v),
                value: formatCurrency(v.totalPaid),
                share: Number(v.totalPaid || 0),
              }))}
              empty={{ icon: Building2, title: 'No vendors paid yet', description: 'Vendors rank here by total paid.' }}
            />
          )}
        </Panel>
        <Panel title="Recent Payments" href={`${SP}?tab=payments`} requires="sp.transactions.view">
          {dashboard.isLoading ? (
            <CardSkeleton height="h-40" />
          ) : (
            <ActivityList
              rows={(dash?.recentPayments ?? []).map((p) => ({
                id: p.id,
                primary: payeeLabel(p.payee),
                secondary: p.referenceNumber,
                value: formatCurrency(p.amount),
                status: p.status,
                meta: formatShortDate(p.createdAt),
              }))}
              empty={{ icon: Users, title: 'No payments yet', description: 'The latest vendor payments show up here.' }}
            />
          )}
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {canSeeRecurring && (
          <Panel title="Upcoming Recurring Payments" href={`${SP}?tab=recurring`} requires="sp.recurring.view">
            {recurring.isLoading ? (
              <CardSkeleton height="h-40" />
            ) : (
              <ActivityList
                rows={upcoming}
                empty={{
                  icon: CalendarClock,
                  title: 'No recurring payments scheduled',
                  description: 'Active recurring payments appear here by next run date.',
                }}
              />
            )}
          </Panel>
        )}
        {invoiceEnabled && canSeeBills && (
          <Panel title="Bills Due" href={`${SP}?tab=create-invoice`} requires="sp.invoices.view">
            {bills.isLoading ? (
              <CardSkeleton height="h-40" />
            ) : (
              <ActivityList
                rows={billsDue}
                empty={{
                  icon: FileText,
                  title: 'No bills awaiting payment',
                  description: 'Approved vendor bills appear here by due date.',
                }}
              />
            )}
          </Panel>
        )}
      </div>
    </div>
  );
}
